// frontend/src/components/Layout/Sidebar.jsx
import React, { useState, useEffect } from 'react';
import { NavLink } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { 
    FaTachometerAlt, 
    FaTasks, 
    FaPlusCircle, 
    FaChartBar, 
    FaLayerGroup, 
    FaUserPlus,
    FaUsers,
    FaCalendarAlt,
    FaChevronLeft,
    FaChevronRight,
    FaUpload
} from 'react-icons/fa';

const Sidebar = ({ onCollapse }) => {
    const { user } = useAuth();
    const [isCollapsed, setIsCollapsed] = useState(false);

    useEffect(() => {
        if (onCollapse) {
            onCollapse(isCollapsed);
        }
    }, [isCollapsed, onCollapse]);

    const toggleCollapse = () => {
        setIsCollapsed(!isCollapsed);
    };

    const menuItems = [
        { path: '/dashboard', icon: FaTachometerAlt, label: 'Dashboard', roles: ['Admin', 'User'] },
        { path: '/tasks', icon: FaTasks, label: 'My Tasks', roles: ['Admin', 'User'] },
        { path: '/upcoming', icon: FaCalendarAlt, label: 'Upcoming', roles: ['Admin', 'User'] },
    ];

    if (user?.role === 'Admin') {
        menuItems.push(
            { path: '/create-task', icon: FaPlusCircle, label: 'Create Task', roles: ['Admin'] },
            { path: '/users', icon: FaUsers, label: 'User Management', roles: ['Admin'] },
            { path: '/users-stats', icon: FaChartBar, label: 'User Stats', roles: ['Admin'] },
            { path: '/templates', icon: FaLayerGroup, label: 'Templates', roles: ['Admin'] },
            { path: '/bulk-upload', icon: FaUpload, label: 'Bulk Upload', roles: ['Admin'] },
        );
    }

    return (
        <div className={`sidebar ${isCollapsed ? 'collapsed' : ''}`}>
            {/* Toggle Button */}
            <button
                onClick={toggleCollapse}
                className="absolute -right-3 top-20 bg-white text-indigo-600 rounded-full p-1.5 shadow-lg hover:scale-110 transition-transform z-10"
            >
                {isCollapsed ? <FaChevronRight size={12} /> : <FaChevronLeft size={12} />}
            </button>

            {/* Logo Section */}
            <div className="p-5">
                {!isCollapsed ? (
                    <>
                        <h2 className="text-xl font-bold text-white">TaskFlow</h2>
                        <p className="text-xs text-indigo-200 mt-1">Task Management System</p>
                    </>
                ) : (
                    <div className="flex justify-center">
                        <div className="w-8 h-8 bg-white/10 rounded-lg flex items-center justify-center">
                            <FaTasks className="text-white text-lg" />
                        </div>
                    </div>
                )}
            </div>
            
            {/* Navigation */}
            <nav className="mt-6 px-3">
                {menuItems.map((item) => (
                    <NavLink
                        key={item.path}
                        to={item.path}
                        className={({ isActive }) =>
                            `flex items-center ${isCollapsed ? 'justify-center' : 'gap-3'} px-3 py-2.5 my-1 rounded-lg transition-all duration-200 ${
                                isActive
                                    ? 'bg-white/10 text-white'
                                    : 'text-indigo-100 hover:bg-white/5 hover:text-white'
                            }`
                        }
                        title={isCollapsed ? item.label : ''}
                    >
                        <item.icon size={18} />
                        {!isCollapsed && <span className="text-sm">{item.label}</span>}
                    </NavLink>
                ))}
            </nav>
            
            {/* User Info */}
            {!isCollapsed && user && (
                <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-white/10 bg-gradient-to-r from-indigo-900/50 to-purple-900/50">
                    <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-white/10 rounded-full flex items-center justify-center">
                            <FaUserPlus size={14} className="text-white" />
                        </div>
                        <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-white truncate">{user.name || user.email}</p>
                            <p className="text-xs text-indigo-200">{user.role}</p>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Sidebar;